/**
 * Shared Schema.org Offer helpers — populates `shippingDetails` and
 * `hasMerchantReturnPolicy` for every Product JSON-LD emitter on the site
 * (client-side SEO.jsx + build-time prerender.js + page-content.js).
 *
 * Values are pinned to the ACTUAL published RCM Biosciences policies:
 *   • Shipping options   → src/data/orderingConfig.js (USPS Ground $15 flat)
 *   • Handling time      → HowToOrder.jsx (order review → invoice → payment
 *                          → ship). Conservative 1–3 business day window.
 *   • Transit time       → USPS Ground Advantage published range (3–5 days)
 *   • Return policy      → src/pages/Terms.jsx §"Returns, Damaged Shipments,
 *                          and Discrepancies" (3-calendar-day damage claim
 *                          window; verified claims resolved by replacement,
 *                          credit, or refund at RCM's discretion).
 *
 * If Terms.jsx or orderingConfig.js change, update this file — do NOT let
 * the structured data drift from the visible policy or Google will flag it.
 *
 * Used by:
 *   • src/components/SEO.jsx           — buildProductSchema()
 *   • scripts/prerender.js             — PDP <script> block + blog product
 *                                        mentions (buildProductMentionSchema)
 *   • scripts/page-content.js          — static page-generation product LD
 *
 * Kept as ES-module + CommonJS export so the Node build scripts can
 * `require()` it while the React app can `import` it.
 */

// Canonical policy URL (§Returns anchor lives on /terms).
const RETURN_POLICY_URL = 'https://rcmbiosciences.com/terms#returns';
const SHIPPING_POLICY_URL = 'https://rcmbiosciences.com/how-to-order#shipping';

/**
 * Google Merchant Listing `shippingDetails` block. Uses USPS Ground
 * Advantage — the cheapest published option and therefore the value
 * Google will show as the minimum shipping charge in rich results.
 * Higher-tier options (Priority, Express) are optional upsells at
 * checkout; representing them here would cause the min-price to jump
 * and mislead shoppers.
 */
function buildShippingDetails() {
  return {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: '15.00',
      currency: 'USD',
    },
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: 'US',
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: 1,
        maxValue: 3,
        unitCode: 'DAY',
      },
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: 3,
        maxValue: 5,
        unitCode: 'DAY',
      },
    },
    // Explicit policy link so the Rich Results Test can follow up.
    url: SHIPPING_POLICY_URL,
  };
}

/**
 * Google Merchant Listing `hasMerchantReturnPolicy` block. Mirrors the
 * Terms.jsx language exactly:
 *   • 3 calendar days from delivery to report damage/discrepancy
 *   • US destinations only
 *   • Verified claims resolved with replacement/credit/refund at RCM's
 *     discretion — customer pays no return fee on approved claims.
 *   • General/change-of-mind returns NOT accepted (schema.org lacks a
 *     "damage-only" enum, so we use FiniteReturnWindow with the 3-day
 *     bound, which is the closest accurate representation Google
 *     accepts. The `merchantReturnLink` sends validators to the exact
 *     clause for the full nuance.)
 */
function buildMerchantReturnPolicy() {
  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: 'US',
    returnPolicyCountry: 'US',
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: 3,
    returnMethod: 'https://schema.org/ReturnByMail',
    returnFees: 'https://schema.org/FreeReturn',
    merchantReturnLink: RETURN_POLICY_URL,
  };
}

/**
 * Convenience — mutates the given `offer` object in place adding the two
 * required Merchant Listing fields. Returns the same reference for chain
 * ergonomics. No-op if the fields are already present (so calling twice
 * is safe).
 */
function attachMerchantListingFields(offer) {
  if (!offer || typeof offer !== 'object') return offer;
  if (!offer.shippingDetails) offer.shippingDetails = buildShippingDetails();
  if (!offer.hasMerchantReturnPolicy) offer.hasMerchantReturnPolicy = buildMerchantReturnPolicy();
  return offer;
}

// Dual export: ES modules for React, CommonJS for Node build scripts.
// Kept as pure CommonJS so both React (via Babel interop) and Node
// (`require()`) consume the identical module. React usage:
//     import { attachMerchantListingFields } from '../data/merchantListingSchema';
// Node usage:
//     const { attachMerchantListingFields } = require('../src/data/merchantListingSchema');
module.exports = {
  buildShippingDetails,
  buildMerchantReturnPolicy,
  attachMerchantListingFields,
};
